const { CommonActions } = require('@react-navigation/native');

class BusinessNavigationService {
  constructor() {
    this.navigationRef = null;
    this.isReady = false;
    this.pendingActions = [];
  }

  // Set navigation reference
  setNavigationRef(ref) {
    this.navigationRef = ref;
    this.isReady = true;
    
    // Execute any pending navigation actions
    this.pendingActions.forEach(action => {
      try {
        action();
      } catch (error) {
        console.error('Error executing pending navigation action:', error);
      }
    });
    this.pendingActions = [];
    
    console.log('✅ BusinessNavigationService initialized');
  }

  // Check if navigation is ready
  isNavigationReady() {
    return this.isReady && this.navigationRef?.current;
  }

  // Execute navigation action or queue it if not ready
  executeOrQueue(action) {
    if (this.isNavigationReady()) {
      try {
        action();
      } catch (error) {
        console.error('Navigation error:', error);
      }
    } else {
      this.pendingActions.push(action);
    }
  }

  // Navigate to a screen
  navigate(screenName, params = {}) {
    this.executeOrQueue(() => {
      this.navigationRef.current?.navigate(screenName, params);
      console.log(`📱 Navigated to ${screenName}`, params);
    });
  }

  // Go back
  goBack() {
    this.executeOrQueue(() => {
      if (this.navigationRef.current?.canGoBack()) {
        this.navigationRef.current?.goBack();
        console.log('📱 Navigated back');
      } else {
        console.log('📱 Cannot go back, at root screen');
      }
    });
  }

  // Reset navigation stack
  reset(routeName, params = {}) {
    this.executeOrQueue(() => {
      this.navigationRef.current?.dispatch(
        CommonActions.reset({
          index: 0,
          routes: [{ name: routeName, params }],
        })
      );
      console.log(`📱 Reset navigation to ${routeName}`);
    });
  }

  // Replace current screen
  replace(screenName, params = {}) {
    this.executeOrQueue(() => {
      this.navigationRef.current?.dispatch(
        CommonActions.replace(screenName, params)
      );
      console.log(`📱 Replaced with ${screenName}`);
    });
  }

  // Business-specific navigation methods
  navigateToBusinessDashboard(params = {}) {
    this.navigate('BusinessDashboard', params);
  }

  navigateToJobManagement(params = {}) {
    this.navigate('PostJob', params);
  }

  navigateToCampaignManagement(params = {}) {
    this.navigate('CampaignList', params);
  }

  navigateToProjectManagement(params = {}) {
    this.navigate('ManageProjects', params);
  }

  navigateToCandidateManagement(params = {}) {
    this.navigate('CandidateManagement', params);
  }

  navigateToInterviewManagement(params = {}) {
    this.navigate('InterviewManagement', params);
  }

  navigateToFinancialManagement(params = {}) {
    this.navigate('FinancialManagement', params);
  }

  navigateToComplianceManagement(params = {}) {
    this.navigate('ComplianceRegulatory', params);
  }

  navigateToMarketingManagement(params = {}) {
    this.navigate('MarketingPromotion', params);
  }

  navigateToBusinessContactProfessional(params = {}) {
    this.navigate('BusinessContactProfessional', params);
  }

  // Professional-specific navigation methods
  navigateToProfessionalDashboard(params = {}) {
    this.navigate('ProfessionalDashboard', params);
  }

  navigateToJobSearch(params = {}) {
    this.navigate('FindJobs', params);
  }

  navigateToJobApplications(params = {}) {
    this.navigate('JobApplications', params);
  }

  navigateToProfile(params = {}) {
    this.navigate('Profile', params);
  }

  navigateToPortfolio(params = {}) {
    this.navigate('Portfolio', params);
  }

  navigateToAnalytics(params = {}) {
    this.navigate('AnalyticsScreen', params);
  }

  navigateToNetworking(params = {}) {
    this.navigate('Networking', params);
  }

  // Learning-specific navigation methods
  navigateToLearningDashboard(params = {}) {
    this.navigate('LearningDashboard', params);
  }

  navigateToLearningCourses(params = {}) {
    this.navigate('LearningCourses', params);
  }

  navigateToCourseDetails(courseId, params = {}) {
    this.navigate('CourseDetails', { courseId, ...params });
  }

  // Utility methods
  getCurrentRoute() {
    if (this.isNavigationReady()) {
      return this.navigationRef.current?.getCurrentRoute();
    }
    return null;
  }

  getCurrentRouteName() {
    const route = this.getCurrentRoute();
    return route?.name || null;
  }

  canGoBack() {
    if (this.isNavigationReady()) {
      return this.navigationRef.current?.canGoBack() || false;
    }
    return false;
  }

  // Navigation with error handling
  safeNavigate(screenName, params = {}) {
    try {
      this.navigate(screenName, params);
      return true;
    } catch (error) {
      console.error(`Failed to navigate to ${screenName}:`, error);
      return false;
    }
  }

  // Navigation with fallback
  navigateWithFallback(primaryScreen, fallbackScreen, params = {}) {
    try {
      this.navigate(primaryScreen, params);
    } catch (error) {
      console.warn(`Failed to navigate to ${primaryScreen}, using fallback ${fallbackScreen}`);
      this.navigate(fallbackScreen, params);
    }
  }

  // Deep linking support
  handleDeepLink(url) {
    try {
      // Parse URL and extract route information
      const route = this.parseDeepLink(url);
      if (route) {
        this.navigate(route.screen, route.params);
        return true;
      }
    } catch (error) {
      console.error('Error handling deep link:', error);
    }
    return false;
  }

  parseDeepLink(url) {
    // Basic deep link parsing - can be extended
    const urlParts = url.split('/');
    if (urlParts.length >= 2) {
      const screen = urlParts[1];
      const params = {};
      
      // Extract parameters from URL
      if (urlParts.length > 2) {
        for (let i = 2; i < urlParts.length; i += 2) {
          if (urlParts[i + 1]) {
            params[urlParts[i]] = urlParts[i + 1];
          }
        }
      }
      
      return { screen, params };
    }
    return null;
  }

  // Navigation analytics
  trackNavigation(screenName, params = {}) {
    console.log('📊 Navigation tracked:', {
      screen: screenName,
      params,
      timestamp: new Date().toISOString(),
      previousScreen: this.getCurrentRouteName(),
    });
  }

  // Batch navigation operations
  navigateSequence(screens) {
    screens.forEach((screen, index) => {
      setTimeout(() => {
        if (typeof screen === 'string') {
          this.navigate(screen);
        } else {
          this.navigate(screen.name, screen.params);
        }
      }, index * 100); // Small delay between navigations
    });
  }
}

// Create singleton instance
const businessNavigationService = new BusinessNavigationService();

module.exports = businessNavigationService;

